import { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle,
  Download,
  Eye,
  FileText,
  Pencil,
  Sparkles,
  X,
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import { useAuth } from '../contexts/AuthContext';
import { InstructorLoadingSkeleton } from '../components/layout/InstructorLoadingSkeleton';
import { InstructorToast } from '../components/layout/InstructorToast';
import { useInstructorToast } from '../components/layout/useInstructorToast';
import {
  instructorService,
  type Dimension,
} from '../services/instructorService';
import { formatEnglishDate, formatEnglishDateTime } from '../lib/dateFormat';
import { getSubmissionFileDisplayNames } from '../lib/submissionFiles';
import '../components/layout/InstructorLayout.css';

interface SubmissionDetailData {
  id: string;
  name: string;
  submitted_at: string;
  status: string;
  file_url: string;
  file_type: string;
  essay_text?: string | null;
  file_urls?: string[] | null;
  file_names?: string[] | null;
  student_name: string;
  student_email: string;
  student_id: string;
  project_title: string;
  project_description: string;
  course_code: string;
  course_title: string;
  dimension_ids?: number[];
  ai_overall_summary?: string | null;
  ai_overall_strengths?: string[] | null;
  ai_priority_improvements?: string[] | null;
  instructor_suggestion?: {
    suggestion_text: string;
    updated_at: string;
  } | null;
}

interface DimensionScoreDetail {
  dimension_id: number;
  score: number;
  ai_feedback_raw?: Record<string, unknown>;
}

const statusClassByStatus: Record<string, string> = {
  submitted: 'instructor-status-submitted',
  scoring: 'instructor-status-scoring',
  scored: 'instructor-status-scored',
  reviewed: 'instructor-status-reviewed',
};

const getStatusClass = (status: string) =>
  statusClassByStatus[status] ?? 'instructor-status-submitted';

const formatStatus = (status: string) =>
  status.charAt(0).toUpperCase() + status.slice(1);

const formatDateTime = (dateString: string) =>
  formatEnglishDateTime(dateString, {
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    month: 'short',
    year: 'numeric',
  });

const SubmissionDetail = () => {
  const { submissionId } = useParams<{ submissionId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [submission, setSubmission] = useState<SubmissionDetailData | null>(null);
  const [scores, setScores] = useState<DimensionScoreDetail[]>([]);
  const [dimensions, setDimensions] = useState<Dimension[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [suggestionText, setSuggestionText] = useState('');
  const [savingSuggestion, setSavingSuggestion] = useState(false);
  const [showRubricDetails, setShowRubricDetails] = useState(false);
  const { showToast, toast } = useInstructorToast();

  const fetchSubmissionDetails = useCallback(async () => {
    if (!submissionId) {
      setError('Submission not found');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await instructorService.getSubmissionDetails(submissionId);
      setSuggestionText(
        data.submission.instructor_suggestion?.suggestion_text || ''
      );
      setSubmission(data.submission);
      setScores(data.scores || []);
    } catch (err) {
      console.error('Error fetching submission details:', err);
      setError('Failed to load submission details');
    } finally {
      setLoading(false);
    }
  }, [submissionId]);

  useEffect(() => {
    fetchSubmissionDetails();
  }, [fetchSubmissionDetails]);

  useEffect(() => {
    const fetchDimensions = async () => {
      try {
        const data = await instructorService.getDimensions();
        setDimensions(data);
      } catch (err) {
        console.error('Error fetching dimensions:', err);
      }
    };

    fetchDimensions();
  }, []);

  const handleSaveSuggestion = async () => {
    if (!submissionId) {
      return;
    }

    if (!suggestionText.trim()) {
      showToast('Please enter a suggestion before saving.', 'error');
      return;
    }

    try {
      setSavingSuggestion(true);
      await instructorService.upsertSubmissionSuggestion(
        submissionId,
        suggestionText.trim()
      );
      await fetchSubmissionDetails();

      const recipientName =
        submission?.student_name.trim().split(/\s+/)[0] || 'student';
      showToast(`Suggestion saved for ${recipientName}`);
    } catch (err) {
      console.error('Error saving suggestion:', err);
      showToast('Failed to save suggestion. Please try again.', 'error');
    } finally {
      setSavingSuggestion(false);
    }
  };

  if (loading) {
    return (
      <div className="instructor-page">
        <InstructorLoadingSkeleton
          message="Loading submission details..."
          variant="detail"
        />
      </div>
    );
  }

  if (error || !submission) {
    return (
      <div className="instructor-page">
        <div className="instructor-error-state">
          <div>
            <div className="mb-4">{error || 'Submission not found'}</div>
            <button
              className="instructor-secondary-button"
              onClick={() => navigate(-1)}
              type="button"
            >
              <ArrowLeft className="h-4 w-4" strokeWidth={2.4} />
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  const selectedDimensionIds =
    submission.dimension_ids && submission.dimension_ids.length > 0
      ? submission.dimension_ids
      : scores.map((score) => score.dimension_id);
  const scoredScores = scores.filter((score) =>
    selectedDimensionIds.includes(score.dimension_id)
  );
  const displayedDimensionIds =
    selectedDimensionIds.length > 0
      ? selectedDimensionIds
      : scores.map((score) => score.dimension_id);
  const dimensionById = new Map(dimensions.map((dimension) => [dimension.id, dimension]));
  const scoreById = new Map(scores.map((score) => [score.dimension_id, score]));
  const averageScore =
    scoredScores.length > 0
      ? (
          scoredScores.reduce((sum, score) => sum + score.score, 0) /
          scoredScores.length
        ).toFixed(1)
      : 'N/A';
  const aiFeedbackAvailable =
    Boolean(submission.ai_overall_summary) ||
    (submission.ai_overall_strengths?.length || 0) > 0 ||
    (submission.ai_priority_improvements?.length || 0) > 0 ||
    scores.some((score) => Boolean(score.ai_feedback_raw));
  const fileList =
    submission.file_urls && submission.file_urls.length > 0
      ? submission.file_urls
      : submission.file_url
        ? [submission.file_url]
        : [];
  const fileNameList = getSubmissionFileDisplayNames(
    fileList,
    submission.file_names,
  );
  const hasEssay = Boolean(submission.essay_text?.trim());
  const hasFiles = fileList.length > 0;

  return (
    <div className="instructor-page">
      <div className="instructor-top-bar">
        <div>
          <button
            className="instructor-secondary-button mb-3"
            onClick={() => navigate(-1)}
            type="button"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={2.4} />
            Back
          </button>
          <h1 className="instructor-page-title">Submission Details</h1>
          <div className="instructor-page-sub">
            {submission.course_code} - {submission.course_title}
          </div>
        </div>
        <span className={`instructor-status-badge ${getStatusClass(submission.status)}`}>
          {formatStatus(submission.status)}
        </span>
      </div>

      <div className="instructor-stat-row">
        <div className="instructor-stat-card navy">
          <div className="instructor-stat-label">Average Score</div>
          <div className="instructor-stat-value">{averageScore}</div>
          <div className="instructor-stat-sub">Out of 3</div>
        </div>
        <div className="instructor-stat-card">
          <div className="instructor-stat-label">Dimensions</div>
          <div className="instructor-stat-value">
            {scoredScores.length}/{selectedDimensionIds.length}
          </div>
          <div className="instructor-stat-sub">Scored dimensions</div>
        </div>
        <div className="instructor-stat-card">
          <div className="instructor-stat-label">AI Feedback</div>
          <div className="instructor-stat-value small">
            {aiFeedbackAvailable ? 'Generated' : 'Pending'}
          </div>
          <div className="instructor-stat-sub">Summary and dimension notes</div>
        </div>
        <div className="instructor-stat-card">
          <div className="instructor-stat-label">Submitted</div>
          <div className="instructor-stat-value small">
            {formatEnglishDate(submission.submitted_at)}
          </div>
          <div className="instructor-stat-sub">{submission.file_type?.toUpperCase()}</div>
        </div>
      </div>

      <div className="instructor-split-grid">
        <aside className="instructor-list-panel">
          <div className="instructor-list-header">
            <div className="instructor-list-title-row">
              <div className="instructor-list-title">
                <span>Submission Context</span>
              </div>
            </div>
          </div>

          <div className="instructor-list-scroll">
            <div className="instructor-feedback-list">
              <div className="instructor-meta-card">
                <div className="instructor-meta-label">Student</div>
                <div className="instructor-meta-value">{submission.student_name}</div>
                <div className="instructor-card-sub">{submission.student_id}</div>
                <div className="instructor-card-sub break-words">{submission.student_email}</div>
              </div>

              <div className="instructor-meta-card">
                <div className="instructor-meta-label">Project</div>
                <div className="instructor-meta-value">{submission.project_title}</div>
                {submission.project_description && (
                  <div className="instructor-card-sub mt-2 max-h-32 overflow-y-auto">
                    {submission.project_description}
                  </div>
                )}
              </div>

              <div className="instructor-meta-card">
                <div className="instructor-meta-label">Submitted</div>
                <div className="instructor-meta-value">
                  {formatDateTime(submission.submitted_at)}
                </div>
                <div className="instructor-card-sub">
                  {submission.name} - {submission.file_type?.toUpperCase()}
                </div>
              </div>

              <div className="instructor-meta-card">
                <div className="instructor-meta-label">Rubric</div>
                <div className="instructor-meta-value">
                  {scoredScores.length} / {selectedDimensionIds.length}
                </div>
                <button
                  className="instructor-secondary-button mt-3 w-full"
                  onClick={() => setShowRubricDetails(true)}
                  type="button"
                >
                  View Rubric
                </button>
              </div>

              {submission.file_url && (
                <button
                  className="instructor-button w-full"
                  onClick={() => window.open(submission.file_url, '_blank')}
                  type="button"
                >
                  <Eye className="h-4 w-4" strokeWidth={2.4} />
                  View Submission File
                </button>
              )}
            </div>
          </div>
        </aside>

        <section className="instructor-detail-panel">
          <div className="instructor-detail-header">
            <div>
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <span className="instructor-badge">Guidance</span>
                <span className="instructor-badge">{submission.course_code}</span>
              </div>
              <h2 className="instructor-detail-title">Review Guidance</h2>
              <div className="instructor-detail-sub">
                AI summary, instructor suggestions, and submitted content
              </div>
            </div>
          </div>

          <div className="instructor-detail-body">
            {(submission.ai_overall_summary ||
              submission.ai_overall_strengths ||
              submission.ai_priority_improvements) && (
              <section className="instructor-note-panel mb-4">
                <div className="mb-3 flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-[var(--instructor-navy)]" strokeWidth={2.3} />
                  <div className="instructor-note-title mb-0">AI Feedback Summary</div>
                </div>

                {submission.ai_overall_summary && (
                  <div className="instructor-note-text mb-4 border-b border-[var(--instructor-border)] pb-4">
                    {submission.ai_overall_summary}
                  </div>
                )}

                {submission.ai_overall_strengths &&
                  submission.ai_overall_strengths.length > 0 && (
                    <div className="mb-4 border-b border-[var(--instructor-border)] pb-4">
                      <div className="mb-2 flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-700" strokeWidth={2.2} />
                        <div className="instructor-note-title mb-0">Strengths</div>
                      </div>
                      <div className="instructor-feedback-list">
                        {submission.ai_overall_strengths.map((strength) => (
                          <div className="instructor-feedback-item" key={strength}>
                            <CheckCircle strokeWidth={2.2} />
                            <div className="instructor-note-text">{strength}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                {submission.ai_priority_improvements &&
                  submission.ai_priority_improvements.length > 0 && (
                    <div>
                      <div className="mb-2 flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4 text-amber-700" strokeWidth={2.2} />
                        <div className="instructor-note-title mb-0">Areas for Improvement</div>
                      </div>
                      <div className="instructor-feedback-list">
                        {submission.ai_priority_improvements.map((improvement) => (
                          <div className="instructor-feedback-item" key={improvement}>
                            <AlertTriangle strokeWidth={2.2} />
                            <div className="instructor-note-text">{improvement}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
              </section>
            )}

            <section className="instructor-note-panel mb-4">
              <div className="mb-3 flex items-center gap-2">
                <Pencil className="h-5 w-5 text-[var(--instructor-navy)]" strokeWidth={2.3} />
                <div className="instructor-note-title mb-0">Instructor Suggestions</div>
              </div>

              {isAdmin ? (
                <div className="instructor-note-text whitespace-pre-wrap">
                  {submission.instructor_suggestion?.suggestion_text ||
                    'No instructor suggestions have been provided yet.'}
                </div>
              ) : (
                <div>
                  <label className="instructor-form-label" htmlFor="instructor-suggestion">
                    Overall improvement suggestions for the student
                  </label>
                  <div className="instructor-form-control">
                    <textarea
                      className="instructor-form-textarea"
                      id="instructor-suggestion"
                      maxLength={5000}
                      onChange={(event) => setSuggestionText(event.target.value)}
                      placeholder="Provide constructive feedback and suggestions to help the student improve their work. This will be visible to the student alongside the AI feedback..."
                      rows={6}
                      value={suggestionText}
                    />
                  </div>
                  <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                    <div className="instructor-form-hint">
                      {suggestionText.length} / 5000 characters
                    </div>
                    {submission.instructor_suggestion && (
                      <div className="instructor-form-hint">
                        Last updated:{' '}
                        {formatEnglishDate(
                          submission.instructor_suggestion.updated_at
                        )}
                      </div>
                    )}
                  </div>
                  <button
                    className="instructor-button mt-4 w-full"
                    disabled={savingSuggestion || !suggestionText.trim()}
                    onClick={handleSaveSuggestion}
                    type="button"
                  >
                    {savingSuggestion
                      ? 'Saving...'
                      : submission.instructor_suggestion
                        ? 'Update Suggestion'
                        : 'Save Suggestion'}
                  </button>
                </div>
              )}
            </section>

            <section className="instructor-card">
              <div className="instructor-card-header">
                <div className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-[var(--instructor-navy)]" strokeWidth={2.3} />
                  <h2 className="instructor-card-title">Student Submission</h2>
                </div>
              </div>

              {!hasEssay && !hasFiles && (
                <div className="instructor-empty-state">No submission content available.</div>
              )}

              {hasEssay && (
                <div className={hasFiles ? 'mb-4 border-b border-[var(--instructor-border)] pb-4' : ''}>
                  <div className="instructor-form-label">Essay / Text</div>
                  <div className="instructor-note-panel max-h-80 overflow-y-auto">
                    <div className="instructor-note-text whitespace-pre-wrap">
                      {submission.essay_text}
                    </div>
                  </div>
                </div>
              )}

              {hasFiles && (
                <div>
                  <div className="instructor-form-label">
                    {fileList.length === 1 ? 'File' : 'Files'}
                  </div>
                  <div className="instructor-feedback-list">
                    {fileList.map((url, index) => {
                      return (
                        <button
                          className="instructor-secondary-button justify-start"
                          key={url}
                          onClick={() => window.open(url, '_blank')}
                          type="button"
                        >
                          <Download className="h-4 w-4" strokeWidth={2.4} />
                          <span className="truncate">{fileNameList[index]}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </section>
          </div>
        </section>
      </div>

      {showRubricDetails && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="instructor-detail-panel max-h-[90vh] w-full max-w-3xl">
            <div className="instructor-detail-header">
              <div>
                <h2 className="instructor-detail-title">Rubric Details</h2>
                <div className="instructor-detail-sub">
                  Scores shown only for selected dimensions
                </div>
              </div>
              <button
                aria-label="Close rubric details"
                className="instructor-icon-button"
                onClick={() => setShowRubricDetails(false)}
                type="button"
              >
                <X className="h-4 w-4" strokeWidth={2.4} />
              </button>
            </div>

            <div className="instructor-detail-body">
              {displayedDimensionIds.length === 0 ? (
                <div className="instructor-empty-state">No dimensions available.</div>
              ) : (
                <div className="instructor-dim-list">
                  {displayedDimensionIds.map((dimensionId) => {
                    const dimension = dimensionById.get(dimensionId);
                    const score = scoreById.get(dimensionId);

                    return (
                      <div className="instructor-dim-item checked disabled" key={dimensionId}>
                        <span
                          className="instructor-dot"
                          style={{ backgroundColor: dimension?.color_hex || '#003d7c' }}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="instructor-dim-name">
                            {dimension ? dimension.label : `Dimension ${dimensionId}`}
                          </span>
                          {dimension?.description && (
                            <span className="instructor-dim-description">
                              {dimension.description}
                            </span>
                          )}
                          <span className="mt-3 block space-y-2">
                            <span className="block text-xs text-[var(--instructor-muted)]">
                              <strong>Level 1:</strong>{' '}
                              {dimension?.rubric_level_1 || 'No rubric provided.'}
                            </span>
                            <span className="block text-xs text-[var(--instructor-muted)]">
                              <strong>Level 2:</strong>{' '}
                              {dimension?.rubric_level_2 || 'No rubric provided.'}
                            </span>
                            <span className="block text-xs text-[var(--instructor-muted)]">
                              <strong>Level 3:</strong>{' '}
                              {dimension?.rubric_level_3 || 'No rubric provided.'}
                            </span>
                          </span>
                        </span>
                        <span
                          className="instructor-dim-chip"
                          style={{
                            backgroundColor: `${dimension?.color_hex || '#003d7c'}14`,
                            borderColor: `${dimension?.color_hex || '#003d7c'}55`,
                            color: dimension?.color_hex || '#003d7c',
                          }}
                        >
                          {score ? `${score.score} / 3` : 'Not scored'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <InstructorToast toast={toast} />
    </div>
  );
};

export default SubmissionDetail;
